import { runInAction } from 'mobx';

import { MouseButtons } from '@app/AppConstants/AppConstants';
import { IVirtualizedRoster, RosterElement } from '../VirtualizedRosterTypes';

import { appStore } from '@app/AppStore';
import { applyDecorators } from '@app/Helpers/Decorator';

import { BaseRosterSelectionStrategy } from './BaseRosterSelectionStrategy';

export class RosterSelectionStrategyDefault extends BaseRosterSelectionStrategy {
    private _rangeStartCell: Element | null = null;
    private _selectionBeforeRange: RosterElement[] | null = null;
    private _totalSelection: RosterElement[] | null = null;

    constructor(roster: IVirtualizedRoster, gridRef: React.RefObject<HTMLDivElement>) {
        super(roster, gridRef);
        applyDecorators(this);
    }

    protected get areGapsEnabled() {
        return true;
    }

    public mouseDownHandler(event: React.MouseEvent<HTMLDivElement>): void {
        if (event.button !== MouseButtons.Left && event.button !== MouseButtons.Right) return;

        const target = event.target as HTMLDivElement;
        if (!target || target.className !== this.cellInnerClass) return;

        const targetElement = this.getElementFromDom(target);
        const isTargetItemSelected = this._roster.selectedCells.some((el) => targetElement && el.personId === targetElement.personId && el.dateUtc === targetElement.dateUtc);
        if (event.button === MouseButtons.Right && isTargetItemSelected) {
            return;
        }

        if (target.closest('.selection-disabled')) {
            return;
        }

        const shouldAddRange = appStore.isCtrlKey && this.areGapsEnabled && event.button !== MouseButtons.Right;
        if (!shouldAddRange) {
            runInAction(() => {
                this._roster.setFocusedElement(targetElement);
                this._roster.selectedCells = [targetElement];
            });
        }

        this._selectionBeforeRange = shouldAddRange ? this._roster.selectedCells : [];
        if (target) {
            this._rangeStartCell = target;
        }
        this._totalSelection = this._getFullSelection(target);

        this.selectItem(target);
    }

    private _getFullSelection(rangeEndCell: Element) {
        const rangeStartCell = this._rangeStartCell;
        if (!rangeStartCell) return [];

        const rangeCells = this.getCellsRange(rangeStartCell, rangeEndCell);
        const totalSelection = this.convertToRosterElements(rangeCells).concat(this._selectionBeforeRange || []);
        return totalSelection;
    }

    public mouseMoveHandler(event: React.MouseEvent<HTMLDivElement>): void {
        const target = event.target as Element;
        if (target && target.className !== this.cellInnerClass) return;

        if (target.closest('.selection-disabled')) {
            return;
        }

        const rangeStartCell = this._rangeStartCell;
        if (!rangeStartCell) return;

        if (rangeStartCell.closest('.selection-disabled')) {
            this._rangeStartCell = target;
        }

        this._totalSelection = this._getFullSelection(target);
        this.updateDomSelection(this._totalSelection);
        if (!this.areGapsEnabled) {
            this._roster.selectedCells = this._totalSelection || [];
        }
    }

    public documentMouseUpHandler(): void {
        if (!this._rangeStartCell) return;
        this._roster.selectedCells = this._totalSelection || [];
        this._rangeStartCell = null;
        this._totalSelection = null;
        this._selectionBeforeRange = null;
    }
}

export class RosterSelectionStrategyDefaultNoGaps extends RosterSelectionStrategyDefault {
    protected get areGapsEnabled() {
        return false;
    }
}
